import { type KeyboardEvent, useMemo, useState } from 'react';

import { pt } from '../copy';
import { yearToHumanLabel } from '../lib/prompt';

type YearChipsProps = {
  years: number[];
  onChange: (next: number[]) => void;
  max?: number;
};

const DEFAULT_MAX = 8;

export default function YearChips({ years, onChange, max = DEFAULT_MAX }: YearChipsProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const canAddMore = years.length < max;
  const parsedDraft = Number.parseInt(draft, 10);
  const canSave = Number.isInteger(parsedDraft);

  const sortedYears = useMemo(() => [...years].sort((a, b) => a - b), [years]);

  const commitDraft = () => {
    if (!canSave || !canAddMore) {
      return;
    }

    const next = Array.from(new Set([...sortedYears, parsedDraft])).sort((a, b) => a - b);
    onChange(next);
    setDraft('');
    setAdding(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    commitDraft();
  };

  return (
    <div className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-white/80">{pt.yearsLabel}</h2>
        <button
          className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium transition hover:bg-white/15 disabled:cursor-not-allowed disabled:bg-white/5 disabled:text-white/30"
          disabled={!canAddMore}
          type="button"
          onClick={() => {
            if (canAddMore) {
              setAdding(true);
            }
          }}
        >
          + {pt.yearsAddEra}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {sortedYears.map((year) => (
          <span
            key={year}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-1.5 text-sm text-white/90"
          >
            <span>{yearToHumanLabel(year)}</span>
            <button
              aria-label={`${pt.yearsRemove} ${yearToHumanLabel(year)}`}
              className="rounded-full bg-white/10 px-1.5 py-0.5 text-xs leading-none transition hover:bg-white/20"
              type="button"
              onClick={() => onChange(sortedYears.filter((candidate) => candidate !== year))}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {adding ? (
        <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
          <label
            className="mb-2 block text-xs font-medium uppercase tracking-[0.24em] text-white/55"
            htmlFor="year-chip-input"
          >
            {pt.yearInputLabel}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-white/25"
              id="year-chip-input"
              inputMode="numeric"
              placeholder={pt.yearInputPlaceholder}
              type="number"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="flex gap-2">
              <button
                className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/45"
                disabled={!canSave}
                type="button"
                onClick={commitDraft}
              >
                {pt.yearInputSave}
              </button>
              <button
                className="rounded-xl bg-white/10 px-3 py-2 text-sm font-medium transition hover:bg-white/15"
                type="button"
                onClick={() => {
                  setDraft('');
                  setAdding(false);
                }}
              >
                {pt.yearInputCancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
